import React from "react";
import cx from "classnames";
import { useForm } from "react-hook-form";
import dayjs from "dayjs";
// eslint-disable-next-line no-restricted-imports
import stylesV2 from "./AppointmentAccountSetup.module.scss";
import stylesV3 from "./AppointmentAccountSetupV2.module.scss";
import { useSchedulingABVariant } from "../utils/useSchedulingABVariant";
import textInputStyles from "components/TextInput/TextInput.module.scss";
import {
  ApptMaskedDateInput,
  ApptMaskedPhoneInput,
} from "components/MaskedTextInput/MaskedTextInput";
import { API_DATE_FORMAT } from "api/constants";
import { Doctor } from "types/doctor";
//import { Patient } from "types/patient";
import { maskPhone } from "components/MaskedTextInput/maskPhone";
import { Office } from "types/office";
import {
  isEmail,
  isValidTLDLength,
  acceptableCharacters,
} from "utils/emailValidation";
import { isValidDateOfBirth } from "utils/dateOfBirthValidation";
import { isValidDateOfBirthVEH } from "utils/dateOfBirthValidationVEH";
import { isValidDOBRestrictVEH } from "utils/isValidDOBRestrictVEH";
import { isValidPhoneNumber } from "utils/phoneNumberValidation";
import { presenceNameValidation } from "utils/presenceNameValidation";
import { sanitizePatientNameInput } from "utils/sanitizePatientNameInput";
import { SmsCheckbox } from "components/SmsCheckbox";
import { ApptTextInput } from "components/TextInput";
import { areExtendedPatientNameFieldsEnabled } from "featureFlags/areExtendedPatientNameFieldsEnabled";

interface AppointmentAccountSetupProps {
  doctor?: Doctor | null;
  office: Office;
  patient: any;
  updatePatient: (fieldValue: any) => void;
  isMobileView: boolean;
  children?: React.ReactNode;
  desktopEmailRowRef?: React.Ref<HTMLDivElement>;
  actionSlotRef?: React.Ref<HTMLDivElement>;
}

export const AppointmentAccountSetup: React.FC<
  AppointmentAccountSetupProps
> = ({
  doctor,
  office,
  patient,
  updatePatient,
  isMobileView,
  children,
  desktopEmailRowRef,
  actionSlotRef,
}) => {
  const { isV3 } = useSchedulingABVariant();
  const styles = isV3 ? stylesV3 : stylesV2;
  const nameMaxLength = areExtendedPatientNameFieldsEnabled() ? 30 : 20;
  const {
    register,
    formState: { errors, isValid },
    setValue,
  } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
  });
  const firstName = register(
    "firstName",
    presenceNameValidation(
      "Not a valid first name; no special characters except '-' allowed",
      nameMaxLength
    )
  );
  const lastName = register(
    "lastName",
    presenceNameValidation(
      "Not a valid last name; no special characters except '-' allowed",
      nameMaxLength
    )
  );
  const phoneNumber = register("phoneNumber", {
    validate: isValidPhoneNumber,
  });

  let dateOfBirth = register("dateOfBirth", {
    validate: isValidDateOfBirth,
  });

  const smsOptInNotices = register("smsOptInNotices");
  const smsOptInPromos = register("smsOptInPromos");

  if (doctor?.offersVEH) {
    if (!office.ageRestrictVEH) {
      dateOfBirth = register("dateOfBirth", {
        validate: isValidDateOfBirthVEH,
      });
    } else {
      dateOfBirth = register("dateOfBirth", {
        validate: isValidDOBRestrictVEH,
      });
    }
  }
  const email = register("email", {
    required: "Not a valid email",
    validate: {
      isEmail: (value) => isEmail(value),
      isValidTLDLength: (value) => isValidTLDLength(value),
      acceptableCharacters: (value) => acceptableCharacters(value),
    },
  });

  return (
    <div className={styles.layout}>
      <h1 className={styles.headline}>Patient Details</h1>
      <hr className={styles.divider} />
      <div className={styles.formContainer}>
        <div
          className={
            !isMobileView ? cx(styles.twoColumns, styles.patientSpace) : ""
          }
        >
        <ApptTextInput
          {...firstName}
          maxLength={nameMaxLength}
          inputType="text"
          onChange={(e) => {
            e.target.value = sanitizePatientNameInput(
              e.target.value,
              nameMaxLength
            );
            updatePatient({
              field: "firstName",
              value: e.target.value,
              isFormValid: isValid,
            });
            firstName.onChange(e);
          }}
          placeholder="Eg- Jacob"
          errorMessage={errors.firstName?.message}
          defaultValue={patient?.firstName}
        >
          First Name <span className={styles.asterisk}> *</span>
        </ApptTextInput>

        <ApptTextInput
          {...lastName}
          maxLength={nameMaxLength}
          inputType="text"
          onChange={(e) => {
            e.target.value = sanitizePatientNameInput(
              e.target.value,
              nameMaxLength
            );
            updatePatient({
              field: "lastName",
              value: e.target.value,
              isFormValid: isValid,
            });
            lastName.onChange(e);
          }}
          placeholder="Eg- John"
          errorMessage={errors.lastName?.message}
          defaultValue={patient?.lastName}
        >
          Last Name <span className={styles.asterisk}> *</span>
        </ApptTextInput>
      </div>

      <div className={styles.desktopTwoColumns} ref={desktopEmailRowRef}>
        <ApptMaskedPhoneInput
          {...phoneNumber}
          onChange={(e) => {
            updatePatient({
              field: "phoneNumber",
              value: e.target.value.replace(/\D/g, ""),
              isFormValid: isValid,
            });
            phoneNumber.onChange(e);
          }}
          setValue={setValue}
          errorMessage={errors.phoneNumber?.message}
          defaultValue={patient?.phoneNumber && maskPhone(patient.phoneNumber)}
          guidedTooltip={
            <p className={styles.tooltipText}>
              Entering a <b>mobile number</b> helps{" "}
              <b>keep your information secure</b> because we can verify your
              identity for future appointments and check-ins by sending you a
              text message.
            </p>
          }
          guidedTooltipClassName={
            errors.phoneNumber?.message && styles.tooltipHavingError
          }
        >
          Phone Number (mobile preferred)
          <span className={styles.asterisk}> *</span>
        </ApptMaskedPhoneInput>
        <ApptTextInput
          {...email}
          inputType="email"
          inputClassName={(textInputStyles as any).inputCustom}
          onChange={(e) => {
            updatePatient({
              field: "email",
              value: e.target.value,
              isFormValid: isValid,
            });
            email.onChange(e);
          }}
          placeholder="Eg- Joye@gmail.com"
          errorMessage={errors.email?.message}
          defaultValue={patient?.email}
        >
          Preferred Email Address <span className={styles.asterisk}> *</span>
        </ApptTextInput>
      </div>
      <div className={!isMobileView ? cx(styles.dob) : ""}>
        <ApptMaskedDateInput
          {...dateOfBirth}
          onChange={(e) => {
            const parsedDate = dayjs(e.target.value);
            let datestr = "";
            if (parsedDate.isValid()) {
              datestr = parsedDate.format(API_DATE_FORMAT);
            } else {
              datestr = e.target.value;
            }
            updatePatient({
              field: "dateOfBirth",
              value: datestr,
              isFormValid: isValid,
            });
            dateOfBirth.onChange(e);
          }}
          errorMessage={errors.dateOfBirth?.message}
          setValue={setValue}
          defaultValue={
            patient?.dateOfBirth &&
            dayjs(patient?.dateOfBirth).format("MM/DD/YYYY")
          }
        >
          Date of Birth <span className={styles.asterisk}> *</span>
        </ApptMaskedDateInput>
      </div>
      <SmsCheckbox
        label="I'd like to receive text reminders about my appointment, about my prescription, and when my glasses or contacts are ready. (MyEyeDr. Notices) *"
        defaultChecked={patient?.smsOptInNotices ?? true}
        {...smsOptInNotices}
        onChange={(e) => {
          updatePatient({
            field: "smsOptInNotices",
            value: e.target.checked,
            isFormValid: isValid,
          });
          smsOptInNotices.onChange(e);
        }}
      />
      <SmsCheckbox
        label="I'd like to receive promotional texts with eyewear and eyecare offers. (MyEyeDr. Promos) *"
        defaultChecked={patient?.smsOptInPromos ?? true}
        {...smsOptInPromos}
        onChange={(e) => {
          updatePatient({
            field: "smsOptInPromos",
            value: e.target.checked,
          });
          smsOptInPromos.onChange(e);
        }}
      />
      </div>
      {children && (
        <div className={styles.actionSlot} ref={actionSlotRef}>
          {children}
        </div>
      )}
    </div>
  );
};
